import { useState } from "react";
import { Clock, Printer } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { useAddDocument } from "../api/documents";
import type { ClusterSigningMode, ClusterType } from "../types";

interface Props {
  clusterId: string;
  signingMode?: ClusterSigningMode;
  needsApproval?: boolean;
  clusterType?: ClusterType;
  onClose: () => void;
  onAdded: () => void;
}

export function AddDocumentDialog({ clusterId, signingMode = "electronic", needsApproval = false, clusterType = "designer_signature", onClose, onAdded }: Props) {
  const isPaper = signingMode === "paper";

  const [title, setTitle] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [files, setFiles] = useState<File[]>([]);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const addMutation = useAddDocument(clusterId);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const file = files[0] ?? null;
    if (!title.trim()) { setError("Podaj tytuł dokumentu."); return; }
    if (!isPaper && !file) { setError("Wybierz plik PDF lub ZIP do sprawdzenia."); return; }
    if (isPaper && quantity < 1) { setError("Ilość musi być co najmniej 1."); return; }
    if (file) {
      const ext = file.name.toLowerCase();
      if (!ext.endsWith(".pdf") && !ext.endsWith(".zip")) {
        setError("Akceptowane formaty: PDF, ZIP.");
        return;
      }
      if (file.size > 500 * 1024 * 1024) {
        setError("Plik jest za duży. Maksymalny rozmiar: 500 MB.");
        return;
      }
    }
    if (notes.length > 4000) {
      setError("Notatka może mieć maks. 4000 znaków.");
      return;
    }

    try {
      await addMutation.mutateAsync({
        title: title.trim(),
        quantity: isPaper ? quantity : undefined,
        original_file: file ?? undefined,
        notes: notes.trim() ? notes : undefined,
      });
      onAdded();
      onClose();
    } catch {
      setError("Nie udało się dodać dokumentu.");
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div role="dialog" className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {isPaper ? "Dodaj dokument papierowy" : "Dodaj dokument do sprawdzenia"}
        </h2>
        {isPaper && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30 px-4 py-3 text-sm text-amber-800 dark:text-amber-300 mb-4">
            <Printer className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Dokument zostanie natychmiast oznaczony jako sprawdzony. Projektant nie otrzyma powiadomienia.
            </span>
          </div>
        )}
        {needsApproval && !isPaper && clusterType !== "director_acceptance" && (
          <div className="flex items-start gap-2 rounded-md border border-orange-200 bg-orange-50 dark:border-orange-700 dark:bg-orange-950/30 px-4 py-3 text-sm text-orange-800 dark:text-orange-300 mb-4">
            <Clock className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Dodany dokument trafi do <strong>kolejki akceptacji</strong>. Dyrektor, koordynator lub CEO muszą go zaakceptować zanim projektant otrzyma powiadomienie i zacznie sprawdzanie.
            </span>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Tytuł dokumentu <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="np. Projekt budowlany — rzut parteru"
            />
          </div>
          {isPaper && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Ilość (mnożnik rozliczeniowy) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min={1}
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-24 border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Liczba kompletów dokumentacji uwzględniana przy rozliczeniu klastra.
              </p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {isPaper
                ? "Skan sprawdzonego dokumentu (opcjonalnie)"
                : <>Plik do sprawdzenia (PDF lub ZIP) <span className="text-red-500">*</span></>
              }
            </label>
            <FileDropZone
              files={files}
              onFilesChange={setFiles}
              accept=".pdf,.zip,application/pdf,application/zip"
            />
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {isPaper
                ? "Możesz dodać skan — plik nie jest wymagany. Maksymalny rozmiar: 500 MB."
                : "Maksymalny rozmiar: 500 MB. Po przesłaniu projektant dostanie powiadomienie."
              }
            </p>
          </div>
          <div>
            <label
              htmlFor="doc-notes"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Notatki <span className="text-xs font-normal text-gray-400">(prywatne — widzi je tylko autor)</span>
            </label>
            <textarea
              id="doc-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={4000}
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="np. Odesłać potem projektantowi branży elektroenergetycznej"
            />
          </div>
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>Anuluj</Button>
            <Button type="submit" disabled={addMutation.isPending}>
              {addMutation.isPending
                ? "Przesyłanie…"
                : isPaper
                  ? "Zapisz dokument"
                  : needsApproval
                    ? "Wyślij do akceptacji"
                    : "Wgraj i wyślij"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
